import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { intakeQuestionsQuery, pendingSetsQuery } from '../api/jedi'
import { useEngagement } from '../lib/engagement'
import { ChatContext } from '../shell/chatContext'
import { GoDeeper } from '../shell/GoDeeper'
import { HomeInstrument } from '../shell/HomeInstrument'
import { HomeProposals } from '../shell/HomeProposals'
import { homeItems } from '../shell/homeRank'
import { ProposalSurface } from '../shell/ProposalSurface'
import { pruneProposalDrafts, useProposalDrafts } from '../shell/proposalDrafts'
import { proposalSearch, useLinkedRecord } from '../shell/recordLink'

export const Route = createFileRoute('/_shell/')({
  component: Home,
  // Plan names a proposal and sends it here to be decided (UX run 08).
  validateSearch: proposalSearch,
})

/**
 * Home — the one actor surface for engagement-level pending proposals (the
 * routing law, B-1, landed for real in wave-4 step 5):
 *
 * - J.E.D.I.-INITIATED arrivals (Origin=intake, and cold start's reads of the
 *   contract) are decided HERE: from their row when nothing on the card is
 *   left to settle, and otherwise in the drawer, which holds the whole card.
 *   They never had a session; Home is where they land. So is an outcome's
 *   agreement asked for from Outcomes.
 * - PM-INITIATED sets are LISTED until decided but ANSWERED in the session
 *   that asked (the Q2 ruling) — their rows open the docked review.
 * - Ask-the-PM questions — intake's honest "I couldn't classify this" —
 *   are rows that pre-seed the docked chat with the statement (the
 *   record-door pattern): the PM's reply drafts through the normal door,
 *   and the attributed Done clears the row while the log keeps it.
 *
 * Above the proposals sit the orientation card and Go deeper (UX run 07):
 * the engagement at a glance, read from the queries the bar reads, and the
 * questions worth asking about it. Neither decides anything; the proposals
 * below are still the only verbs on the page.
 */
function Home() {
  const { open, openSeeded } = useContext(ChatContext)
  const { id: engagementId } = useEngagement()
  const sets = useQuery(pendingSetsQuery(engagementId))
  const questions = useQuery(intakeQuestionsQuery(engagementId))
  const [note, setNote] = useState<string | null>(null)
  /*
    A PROPOSAL OPENS IN THE DRAWER (RFC-0016 decision 5; ruled 2026-09-22).
    The drawer puts the conversation beside the set, where the question "where
    did you see this?" can actually be put before deciding.
  */
  const [openSetId, setOpenSetId] = useState<string | null>(null)

  // A review draft follows its proposal between Home, the drawer and the
  // chat, and goes when the proposal leaves the pending list.
  const { drafts } = useProposalDrafts(engagementId)
  useEffect(() => {
    if (sets.data) pruneProposalDrafts(engagementId, sets.data.map((set) => set.id!))
  }, [engagementId, sets.data])
  // The ranked queue (the Work rubric's urgency, then outcome drift), and the
  // drawer's Previous and Next walk it in the order the list shows.
  const items = homeItems(sets.data ?? [], questions.data ?? [], drafts)
  const everySet = items.flatMap((item) => (item.kind === 'proposal' ? [item.set] : []))
  const openIndex = everySet.findIndex((s) => s.id === openSetId)
  const openSet = openIndex >= 0 ? everySet[openIndex]! : null
  // A proposal Plan pointed at opens in the drawer once it has loaded, once.
  const { set: linkedSet } = Route.useSearch()
  useLinkedRecord(linkedSet, sets.data ? everySet : undefined, (s) => s.id!, (s) => setOpenSetId(s.id!))

  return (
    <section className="surface surface-home">
      {/*
        THE ORIENTATION CARD AND GO DEEPER come first, and the page carries no
        heading of its own (UX run 07): the reference opens straight onto the
        card, and the rail already says where you are. The heading stays for a
        screen reader, which has no rail to look at.
      */}
      <h1 className="sr-only">Home</h1>
      <HomeInstrument onOpenSet={setOpenSetId} quiet={!!(sets.error ?? questions.error)} />
      <GoDeeper />
      <HomeProposals
        // A new engagement is a new list: nothing decided on the last one
        // stays on screen.
        key={engagementId}
        items={items}
        drafts={drafts}
        loading={sets.isPending || questions.isPending}
        error={sets.error ?? questions.error}
        note={note}
        onOpenSet={setOpenSetId}
        onReviewInSession={open}
        onAnswer={(question) => openSeeded(`${question.statement}: `, question.id)}
      />

      {/*
        The drawer for whichever proposal was opened. Previous and Next walk the
        pending queue in the order Home lists it, so a person can decide a run
        of them without going back out to the list between each.
      */}
      {openSet && (
        <ProposalSurface
          set={openSet}
          position={{ i: openIndex + 1, n: everySet.length }}
          onClose={() => setOpenSetId(null)}
          onPrev={openIndex > 0 ? () => setOpenSetId(everySet[openIndex - 1]!.id) : undefined}
          onNext={openIndex < everySet.length - 1
            ? () => setOpenSetId(everySet[openIndex + 1]!.id)
            : undefined}
          onDecided={(message, remainderId) => {
            setNote(message)
            // Decided, so it is no longer pending: the drawer closes on the act
            // rather than leaving a card that has already been answered. Decided
            // in part (decide by subject), it moves to the rest, which is still
            // waiting on the person, and opens as soon as the list holds it.
            setOpenSetId(remainderId ?? null)
          }}
        />
      )}
    </section>
  )
}
